import { Badge } from "@/components/ui/badge";

export interface SelectionGridItem {
  name: string;
  description?: string;
  arguments?: Array<{ name: string; required?: boolean }>;
}

export interface SelectionGridProps<T extends SelectionGridItem> {
  items: T[];
  selectedItem: string | null;
  onItemSelect: (itemName: string) => void;
  isLoading: boolean;
  error: string | null;
  emptyMessage: string;
  errorMessage: string;
  renderItemContent?: (item: T, isSelected: boolean) => React.ReactNode;
}

function DefaultItemContent<T extends SelectionGridItem>(
  item: T,
  _isSelected: boolean,
) {
  return (
    <>
      <div className="font-semibold text-foreground group-hover:text-primary transition-colors">
        {item.name}
      </div>
      <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
        {item.description || "No description provided"}
      </div>
      {item.arguments && item.arguments.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {item.arguments.map((arg) => (
            <Badge key={arg.name} variant="outline" className="text-xs">
              {arg.name}
              {arg.required && <span className="text-destructive ml-1">*</span>}
            </Badge>
          ))}
        </div>
      )}
    </>
  );
}

function LoadingGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: it's ok. no need to complain
          key={i}
          className="h-24 bg-muted animate-pulse rounded-lg"
        />
      ))}
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return <div className="text-center py-8 text-destructive">{message}</div>;
}

function EmptyMessage({ message }: { message: string }) {
  return (
    <div className="text-center py-8 text-muted-foreground">{message}</div>
  );
}

export function SelectionGrid<T extends SelectionGridItem>({
  items,
  selectedItem,
  onItemSelect,
  isLoading,
  error,
  emptyMessage,
  errorMessage,
  renderItemContent = DefaultItemContent,
}: SelectionGridProps<T>) {
  if (isLoading) {
    return <LoadingGrid />;
  }

  if (error) {
    return <ErrorMessage message={errorMessage} />;
  }

  if (items.length === 0) {
    return <EmptyMessage message={emptyMessage} />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((item) => {
        const isSelected = selectedItem === item.name;

        return (
          <button
            key={item.name}
            type="button"
            className={`p-4 rounded-lg border transition-all hover:bg-muted hover:shadow-md text-left group ${
              isSelected ? "border-primary bg-primary/5 shadow-md" : ""
            }`}
            onClick={() => onItemSelect(item.name)}
          >
            {renderItemContent(item, isSelected)}
          </button>
        );
      })}
    </div>
  );
}
